// Cronograma de UNA obra (mini-Gantt de la ficha): geometría pura y testeable.
//
// Eje: del primer al último hito con fecha (inicio, hitos, entregas, montaje y apertura),
// con un margen a cada lado y "hoy" dentro si está cerca. Barra de obra (inicio ->
// apertura), barra de montaje de mobiliario (inicio -> fin) y un marcador por fecha.
import { MILESTONE_TYPES, isValidIsoDate } from "./agendaModel";
import { hitoState, isCriticalHito } from "../hitosModel";

const DAY = 86400000;
const toUtc = (iso) => {
    const [y, m, d] = iso.split("-").map(Number);
    return Date.UTC(y, m - 1, d);
};
const isoOfDate = (date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
const MONTHS = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];
// Se incluye "hoy" en el eje si cae a menos de este margen de las fechas de la obra.
const TODAY_REACH_DAYS = 120;

export function buildObraTimeline(obra, today) {
    const todayIso = isoOfDate(today);
    const items = MILESTONE_TYPES
        .filter((type) => isValidIsoDate(obra?.[type.id]))
        .map((type) => ({
            id: type.id,
            prefix: type.prefix,
            label: type.label,
            date: obra[type.id],
            hito: isCriticalHito(type.id) ? hitoState(obra, type.id, today) : null,
        }));
    if (!items.length) return { empty: true, items: [], ticks: [] };

    const times = items.map((item) => toUtc(item.date));
    let min = Math.min(...times);
    let max = Math.max(...times);
    const todayTime = toUtc(todayIso);
    if (todayTime >= min - TODAY_REACH_DAYS * DAY && todayTime <= max + TODAY_REACH_DAYS * DAY) {
        min = Math.min(min, todayTime);
        max = Math.max(max, todayTime);
    }
    // Margen del 4 % (mínimo 7 días) para que ningún marcador quede pegado al borde.
    const pad = Math.max(7 * DAY, (max - min) * 0.04);
    const start = min - pad;
    const end = max + pad;
    const pct = (time) => Math.round(((time - start) / (end - start)) * 10000) / 100;

    const span = (fromId, toId) => {
        if (!isValidIsoDate(obra?.[fromId]) || !isValidIsoDate(obra?.[toId])) return null;
        const from = toUtc(obra[fromId]);
        const to = toUtc(obra[toId]);
        if (to < from) return null;
        return { left: pct(from), width: Math.max(0.5, pct(to) - pct(from)), days: Math.round((to - from) / DAY) };
    };

    // Marcas de mes: una por mes (o cada 2/3 meses si el eje es largo).
    const ticks = [];
    const startDate = new Date(start);
    const months = Math.max(1, Math.round((end - start) / (30.4 * DAY)));
    const step = months > 24 ? 3 : months > 12 ? 2 : 1;
    for (let cursor = new Date(Date.UTC(startDate.getUTCFullYear(), startDate.getUTCMonth() + 1, 1)); cursor.getTime() <= end; cursor = new Date(Date.UTC(cursor.getUTCFullYear(), cursor.getUTCMonth() + step, 1))) {
        const month = cursor.getUTCMonth();
        ticks.push({
            key: `${cursor.getUTCFullYear()}-${month}`,
            label: month === 0 || !ticks.length ? `${MONTHS[month]} ${String(cursor.getUTCFullYear()).slice(2)}` : MONTHS[month],
            left: pct(cursor.getTime()),
        });
    }

    return {
        empty: false,
        todayIso,
        start: new Date(start).toISOString().slice(0, 10),
        end: new Date(end).toISOString().slice(0, 10),
        today: todayTime >= start && todayTime <= end ? pct(todayTime) : null,
        obraBar: span("inicioObra", "apertura"),
        montajeBar: span("inicioMontajeMob", "finMontajeMob"),
        items: items.map((item) => ({ ...item, left: pct(toUtc(item.date)) })),
        ticks,
    };
}

// ── Calendario de la ficha (pestaña Agenda) ─────────────────────────────────
// Mes con el que se abre: el del próximo evento desde hoy; si todos pasaron, el del
// último; sin eventos, el de hoy. `events`: [{ date: "AAAA-MM-DD" }].
export function initialAgendaMonth(events, today) {
    const todayIso = isoOfDate(today);
    const dates = events.map((event) => event.date).filter(Boolean).sort();
    const pick = dates.find((date) => date >= todayIso) || dates[dates.length - 1];
    if (!pick) return { year: today.getFullYear(), month: today.getMonth() };
    const [year, month] = pick.split("-").map(Number);
    return { year, month: month - 1 };
}

// Meses con eventos (para saltar a ellos), en orden: [{ year, month, count }].
export function monthsWithEvents(events) {
    const counts = new Map();
    events.forEach((event) => {
        if (!event.date) return;
        const key = event.date.slice(0, 7);
        counts.set(key, (counts.get(key) || 0) + 1);
    });
    return [...counts.entries()].sort(([a], [b]) => a.localeCompare(b)).map(([key, count]) => {
        const [year, month] = key.split("-").map(Number);
        return { year, month: month - 1, count };
    });
}
